import { summarizeEfficiencyBasis } from '@/utils/efficiencyDisclosure';
import { useMemo } from 'react';
// DCC-A2: dropped `format` from date-fns import — the 3 production
// `format(selectedDate, 'yyyy-MM-dd')` sites at L38/L62/L75 (pre-edit)
// were migrated to use the canonical `selectedDayLabel` string from
// DateContext directly. `getDay(selectedDate)` is preserved (DCD-A3
// OUT_OF_SCOPE: day-of-week index extraction, not date-label
// derivation; selectedDate Date object remains the canonical input
// for date-fns weekday helpers).
import { useDate } from '@/contexts/DateContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Activity, Gauge } from 'lucide-react';
import { cn } from '@/lib/utils';
import { calculateOBLI, formatOBLIScore, getOBLIColorClasses, OBLI_CAPACITY_UNAVAILABLE_LABEL } from '@/utils/obliCalculation';
// DMC-D3c-1: canonical deleted-state helper — applied in assembleOBLIDay (src/lib/obli/obliDayInputs.ts).
// Phase G2B — canonical live-load helpers. Workload base now reflects
// live projected workload (matches Shop Utilization G2A + Overview Tech
// Workload G1 + Dayboard tech-column header Phase C). OBLI penalty
// weights are unchanged.
import { clock } from '@/utils/clock';
import { assembleOBLIDay } from '@/lib/obli/obliDayInputs';
// CSI-S4: canonical per-day hours reader. Replaces inline
// `tech.hours?.[dayOfWeek]` reads in the workingTechs filter +
// totalCapacity reducer below. Null-preserved: missing per-day entry
// still resolves to "tech not working today" (filter excludes / reducer
// skips); NO flat-column fallback (this surface intentionally treats
// no-hours-configured as not-working, matching pre-CSI-S4 semantics).

interface DayboardLoadIndicatorProps {
  technicians: any[];
  /** Work-role answer: whose time is MECHANICAL capacity (null/absent = everyone). */
  mechanicalTechnicianIds?: ReadonlySet<string> | null;
  tickets: any[];
  selectedDate: Date;
  shopHours?: any;
  holidays?: string[];
  bays?: any[];
  /**
   * True once the roster AND the work-role answer for this shop are known.
   * Until then (or when either read failed) capacity is not a measurement and
   * the index shows "Capacity unavailable" — never 0, never 100.
   */
  capacityInputsReady?: boolean;
}

export function DayboardLoadIndicator({
  technicians,
  tickets,
  selectedDate,
  shopHours,
  holidays = [],
  bays = [],
  mechanicalTechnicianIds = null,
  capacityInputsReady = true,
}: DayboardLoadIndicatorProps) {
  // DCC-A2: pulled `selectedDayLabel` to function scope so the three
  // useMemo blocks below (isShopOpen / workingTechs / totalCapacity)
  // can use it directly for date-string derivation. Pre-DCC-A2 each
  // useMemo derived `dateStr` via `format(selectedDate, 'yyyy-MM-dd')`
  // — equivalent in behavior when browser-tz === shop-tz, but
  // doctrinally indirect: `selectedDayLabel` is the canonical SoT
  // (DateContext doc-block: "Do NOT use this for database queries"
  // for the Date object). DCD-A3 + CSI-S4 already widened this file's
  // useDate() destructure to include `selectedDayLabel`; DCC-A2
  // completes the migration by routing all 3 dateStr derivations
  // through the canonical string. `getDay(selectedDate)` for day-of-
  // week index extraction is preserved (DCD-A3 OUT_OF_SCOPE).
  const { selectedDayLabel } = useDate();

  // Core-100 R4 (CORE-AV): every input of the load index comes from the ONE assembler the calendar bucket also
  // uses (assembleOBLIDay) — same day tickets, same mechanical roster, same capacity, same staffing-in-minutes —
  // so the board and the calendar cannot show two different loads for one shop-day.
  const day = useMemo(() => assembleOBLIDay({
    technicians, tickets, selectedDate, dayLabel: selectedDayLabel, shopHours, holidays,
    mechanicalTechnicianIds, capacityInputsReady, nowMs: clock.nowMs(),
  }), [technicians, tickets, selectedDate, selectedDayLabel, shopHours, holidays, mechanicalTechnicianIds, capacityInputsReady]);
  const { dayTickets, workingTechs, unknownHoursCount, totalCapacity } = day;
  const { bookedMinutes, isShopOpen } = day.input;
  const workloadPercent = totalCapacity > 0 ? Math.round((bookedMinutes / totalCapacity) * 100) : 0;
  const obliResult = useMemo(() => calculateOBLI(day.input), [day]);

  const efficiencyBasis = useMemo(() => summarizeEfficiencyBasis(workingTechs), [workingTechs]);

  const obliScore = obliResult.score;
  const unavailable = obliResult.state === 'unavailable';
  const colors = getOBLIColorClasses(unavailable ? 0 : obliScore);

  const getWorkloadColor = (percent: number) => {
    if (percent <= 60) return 'text-success';
    if (percent <= 85) return 'text-warning';
    if (percent <= 100) return 'text-orange-600';
    return 'text-destructive';
  };

  if (!isShopOpen) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/50 text-muted-foreground text-xs">
        Shop Closed
      </div>
    );
  }

  if (unavailable) {
    return (
      <div
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-muted bg-muted/50 text-muted-foreground text-xs"
        data-testid="obli-capacity-unavailable"
        title={obliResult.description}
      >
        <Gauge className="h-3.5 w-3.5" />
        <span className="font-medium">OBLI: {formatOBLIScore(obliResult)}</span>
        <span>· {OBLI_CAPACITY_UNAVAILABLE_LABEL}</span>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn(
            "flex items-center gap-3 px-3 py-1.5 rounded-md border cursor-default",
            "w-full sm:w-auto",
            colors.bg,
            colors.border
          )}>
            <div className="flex items-center gap-1.5">
              <Gauge className={`h-3.5 w-3.5 ${colors.text}`} />
              <span className={`text-xs font-medium ${colors.text}`} data-testid="obli-score" data-obli-state={obliResult.state}>
                OBLI: {obliScore}
              </span>
            </div>
            <div className="w-px h-4 bg-border" />
            <div className="flex items-center gap-1.5">
              <Activity className={`h-3.5 w-3.5 ${getWorkloadColor(workloadPercent)}`} />
              <span className={`text-xs font-medium ${getWorkloadColor(workloadPercent)}`}>
                {workloadPercent}%
              </span>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          <div className="space-y-1">
            <p><strong>{workingTechs.length}</strong> techs working</p>
            <p>
              <strong>{Math.round(bookedMinutes)}</strong> min live load / <strong>{Math.round(totalCapacity)}</strong> min {unknownHoursCount > 0 ? 'known ' : ''}capacity
            </p>
            {/*
              CORE-AW §44: the capacity above is the sum of what is known. A
              technician with no recorded hours is neither working nor off,
              and the banner says which of the two it cannot tell.
            */}
            {unknownHoursCount > 0 && (
              <p className="text-muted-foreground text-[10px]" data-testid="dayboard-capacity-incomplete">
                {unknownHoursCount} technician{unknownHoursCount === 1 ? "'s" : "s'"} working hours not set — capacity unknown, excluded
              </p>
            )}
            <p className="text-muted-foreground text-[10px]">
              Live load = scheduled wall time + run-long + bay-blocking pause
            </p>
            {/* Core-100 R2 CORE-AV-GAP-001: what the load's OptiTime rests on. */}
            <p className="text-muted-foreground text-[10px]" data-testid="dayboard-load-efficiency-basis">
              {efficiencyBasis.sentence}
            </p>
            <p><strong>{dayTickets.length}</strong> active tickets</p>
            {obliResult.state === 'no_scheduled_capacity' && (
              <p className="text-destructive" data-testid="obli-no-scheduled-capacity">{obliResult.description}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
